<template>
  <div :style="offsetStyle" class="action-bar">
    <div class="d-flex">
      <div class="flex-fill">
        <portal-target name="project-action-bar" />
      </div>

      <div>
        <template v-if="!$route.name.includes('.edit') && !project.pendingChangeRequests.length">
          <template v-for="(action, index) in project.action">
            <b-button
              v-if="action.type === 'advance'"
              :key="index"
              variant="primary"
              class="action-button mr-2"
              :disabled="advanceButtonBusy || rejectButtonBusy"
              @click="advance(action.buttonText, action.confirmation)"
            >
              <b-spinner v-if="advanceButtonBusy" class="mr-1" small />
              {{ action.buttonText }}
            </b-button>

            <b-button
              v-else
              :key="index"
              v-b-modal="action.confirmation === 'REJECT_ADVANCE_FUND_CONFIRM' ? 'advance-fund-reject-modal' : 'proposal-reject-modal'"
              variant="danger"
              class="action-button"
              :disabled="rejectButtonBusy || advanceButtonBusy"
            >
              <b-spinner v-if="rejectButtonBusy" class="mr-1" small />
              {{ action.buttonText }}
            </b-button>
          </template>

          <template v-if="$auth.check('coordinator') && status === 'Approved'">
            <button v-if="!project.eventUrl" type="button" class="btn btn-warning" @click="createEvent">สร้างกิจกรรมบนหน้าเว็บ</button>

            <form v-else :action="'https://event.osda.kmitl.ac.th/sso?eventId=' + project.event.id" method="POST" target="_blank" style="display: inline-block;">
              <input type="hidden" name="token" :value="$auth.token()">
              <button type="submit" class="btn btn-outline-warning">แก้ไขกิจกรรมบนหน้าเว็บ</button>
            </form>
          </template>
        </template>

        <template v-if="!$route.name.includes('.edit') && project.pendingChangeRequests.length">
          <router-link :to="{ name: 'projects.show.change-requests.index', params: $route.params }" active-class="active">
            <a href="#" class="d-inline-block text-danger pt-1">มีรายการเปลี่ยนแปลงแก้ไขรอการพิจารณา</a>
          </router-link>
        </template>
      </div>
    </div>

    <proposal-reject-modal @confirm="reject" />

    <advance-fund-confirm-dialog
      :requested-amount="project.funding.internal.advance"
      @closed="advanceButtonBusy = false"
    />
    <advance-fund-reject-dialog @confirm="reject" />
    <advance-project-closure-advisor-comment-dialog
      @closed="advanceButtonBusy = false"
    />
  </div>
</template>

<script>
import { ROLE_OSDA, ROLE_COORDINATOR } from '@/constants'
import ProposalRejectModal from '@/components/project/ProposalRejectModal'
import AdvanceFundConfirmDialog from '@/components/project/AdvanceFundConfirmDialog'
import AdvanceFundRejectDialog from '@/components/project/AdvanceFundRejectDialog'
import AdvanceProjectClosureAdvisorCommentDialog from '@/components/project/AdvanceProjectClosureAdvisorCommentDialog'

export default {
  components: {
    ProposalRejectModal, AdvanceFundConfirmDialog, AdvanceFundRejectDialog, AdvanceProjectClosureAdvisorCommentDialog
  },
  props: {
    subNavOffset: { type: Number, required: true }
  },
  data: () => ({
    advanceButtonBusy: false,
    rejectButtonBusy: false
  }),
  computed: {
    status () { return this.$store.getters['project/getProject'](this.$route.params.id).status },
    project () { return this.$store.getters['project/getProject'](this.$route.params.id) },
    isSidebarMinimized () { return this.$store.state.layout.isSidebarMinimized },
    isSidebarEnabled () { return this.$store.state.layout.isSidebarEnabled },
    offsetStyle () {
      let offset = this.subNavOffset
      if (!this.isSidebarEnabled) { return { left: offset + 'px' } }
      return this.isSidebarMinimized ? { left: offset + 45 + 'px' } : { left: offset + 220 + 'px' }
    }
  },
  methods: {
    async advance (buttonText, confirmationDialog) {
      this.advanceButtonBusy = true
      const project = this.$store.getters['project/getProject'](this.$route.params.id)

      // if (project.status === 'Approved') {
      //   this.submitForClosure()
      //   return
      // }

      if (this.$auth.check(ROLE_OSDA)) {
        // Check if document from e-Office has been uploaded
        if (!project.signedDoc) {
          await this.$bvModal.msgBoxOk('กรุณาอัพโหลดไฟล์เอกสารจาก e-Office ก่อนอนุมัติโครงการ', {
            okTitle: 'ปิด',
            centered: true
          })
          this.advanceButtonBusy = false
          return
        }

        if (project.status === 'WaitingForOsdaApproval' && !project.isCheckIssuingPossible) {
          if (!await this.$bvModal.msgBoxConfirm(
            'โครงการนี้จะไม่ได้รับการพิจารณาออกเช็ค เนื่องจากมีโครงการอื่นในหน่วยงานนี้ยังไม่ผ่านการอนุมัติปิดโครงการ',
            {
              title: 'แจ้งเตือนเงื่อนไขการออกเช็ค?',
              okTitle: 'ตกลง',
              cancelTitle: 'ปิด',
              centered: true
            }
          )) {
            this.advanceButtonBusy = false
            return
          }
        }
      }

      switch (confirmationDialog) {
        case 'ADVANCE_FUND_CONFIRM':
          this.$bvModal.show('advance-fund-confirm')
          return
        case 'ADVANCE_CLOSE_ADVISOR_COMMENT_CONFIRM':
          this.$bvModal.show('advance-close-advisor-comment-confirm')
          return
        default:
          if (!await this.$bvModal.msgBoxConfirm(
            `ยืนยันการ "${buttonText}" หรือไม่?`,
            {
              title: 'ยืนยันการดำเนินการ',
              okTitle: 'ส่ง',
              cancelTitle: 'ยกเลิก',
              centered: true,
              okVariant: 'primary'
            }
          )) {
            this.advanceButtonBusy = false
            return
          }
      }

      try {
        await this.$store.dispatch('project/advance', this.$route.params.id)
        this.$bvToast.toast(this.$auth.check(ROLE_COORDINATOR) ? project.status === 'Approved' ? 'เริ่มร่างคำขออนุมัติปิดโครงการสำเร็จ' : 'ส่งโครงการให้ประธานโครงการพิจารณาสำเร็จ' : 'ดำเนินการสำเร็จ', {
          title: 'แจ้งเตือน',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.log(error)
        this.$bvToast.toast('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาติดต่อผู้ดูแลระบบ', {
          title: 'แจ้งเตือน',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.advanceButtonBusy = false
      }
    },
    async reject (reason) {
      this.rejectButtonBusy = true
      try {
        await this.$store.dispatch('project/reject', { id: this.$route.params.id, reason })
        this.$bvToast.toast('เปลี่ยนสถานะโครงการสำเร็จ', {
          title: 'แจ้งเตือน',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.log(error)
        let { graphQLErrors } = error
        this.errorMessage = 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาติดต่อผู้ดูแลระบบ'
        if (graphQLErrors[0].extensions.category === 'custom') {
          this.errorMessage = graphQLErrors[0].message
        }
        this.$bvToast.toast(this.errorMessage, {
          title: 'แจ้งเตือน',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.rejectButtonBusy = false
      }
    },
    async submitForClosure () {
      if (!await this.$bvModal.msgBoxConfirm(
        'เมื่อส่งแบบประเมินแล้ว จะไม่สามารถแก้ไขข้อมูลในส่วนการประเมินผลโครงการได้ ยืนยันการส่งหรือไม่?',
        {
          title: 'ยืนยันการส่งแบบประเมินโครงการ?',
          okTitle: 'ยืนยัน',
          cancelTitle: 'ปิด',
          centered: true
        }
      )) {
        return
      }

      try {
        await this.$store.dispatch('project/advance', this.$route.params.id)

        this.$bvToast.toast('ส่งแบบประเมินสำเร็จ', {
          title: 'สำเร็จ',
          variant: 'success',
          solid: true,
          noCloseButton: true
        })
      } catch (_) {
        this.$bvToast.toast('เกิดข้อผิดพลาดในการบันทึกข้อมูล ตรวจสอบการกรอกข้อมูลอีกครั้ง', {
          title: 'แจ้งเตือน',
          variant: 'danger',
          solid: true,
          noCloseButton: true
        })
      }
    },
    async createEvent () {
      try {
        await this.$store.dispatch('project/createEvent', this.$route.params.id)
        this.$bvToast.toast('สร้างกิจกรรมบนหน้าเว็บสำเร็จ', {
          title: 'แจ้งเตือน',
          variant: 'success',
          solid: true
        })
        window.location.reload()
      } catch (error) {
        console.log(error)
        this.$bvToast.toast('เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาติดต่อผู้ดูแลระบบ', {
          title: 'แจ้งเตือน',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.rejectButtonBusy = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.action-bar {
  border-bottom: $card-border-width solid $card-border-color;
  border-top: $card-border-width solid $card-border-color;
  background-color: $white;
  position: fixed;
  top: 151px;
  right: 0;
  left: 285px;
  padding: 8px 15px;
  background-color: #fafafa;
  z-index: 1;
  height: 53px;

  .status-chart {
    flex: 1;
    padding: 5px 0 3px 0;
  }

  .main-action, .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 10px;
  }

  .main-action {
    border-left: 1px solid $card-border-color;
    border-right: 1px solid $card-border-color;
  }

  .actions, .main-action {
    .action-button:not(:last-child) {
      margin-right: 10px;
    }
  }
}
</style>
